import { createElement, Fragment, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { createRoot, Root } from 'react-dom/client'

import { LiteClient } from 'algoliasearch/lite'
import type { AutocompleteOptions } from '@algolia/autocomplete-js'
import * as autoAll from '@algolia/autocomplete-js'
const { autocomplete } = autoAll

import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { useSearchBox } from 'react-instantsearch'
import { router } from '@inertiajs/react'
import { useLocalStorage } from 'usehooks-ts'

import ChatBoxIcon from '@/icons/bwi/chat.svg'
import { interpolate, localizedPath, useLocale, useTranslations } from '@/utils/locale'

import './search-theme.css'
import { useSearch } from '../search-provider'

type AutocompleteProps = Partial<AutocompleteOptions<Record<string, unknown>>> & {
  searchClient: LiteClient
  indexName: string
  updateOnChange?: boolean
  classNames?: AutocompleteOptions<Record<string, unknown>>['classNames'] & {
    bwSearchContainer?: string
    bwPanelContainer?: string
  }
}

// The autocomplete panel is rendered into a detached React root via createRoot,
// outside the Inertia provider. Hooks that depend on Inertia context (usePage,
// useTranslations) cannot be called inside this component — translations must
// be pre-computed in the parent (which IS inside Inertia) and passed as props.
type AskAiSuggestionProps = {
  query: string
  titleText: string
  headingNode: ReactNode
  descriptionText: string
  askAiHref: string
  agentType?: 'help' | 'sales'
}

function AskAiSuggestion({
  query,
  titleText,
  headingNode,
  descriptionText,
  askAiHref,
  agentType = 'help',
}: AskAiSuggestionProps) {
  const [, setAskAiQuery] = useLocalStorage<string | undefined>('askAiQuery', undefined)
  const { trackChatOpen } = useSearch()
  return (
    <div className="p-[6px] border-b border-secondary-100">
      <button
        type="button"
        title={titleText}
        className="w-full text-left flex items-center rounded hover:bg-[rgba(var(--aa-selected-color-rgb),var(--aa-selected-color-alpha))]! px-3! py-2!"
        onClick={(e) => {
          e.preventDefault()
          setAskAiQuery(query)
          router.visit(askAiHref)
          trackChatOpen('search-suggestion', { agent: agentType })
          return false
        }}
        onAuxClick={(e) => {
          e.preventDefault()
          setAskAiQuery(query)
          window.open(askAiHref, '_blank', 'noreferrer,noopener')

          trackChatOpen('search-suggestion', { agent: agentType })
          return false
        }}
      >
        <ChatBoxIcon className="w-5 h-5 mr-3 text-primaryBlue" />
        <div className="flex-1">
          <h4 className="mb-0 text-base text-neutral-800 dark:text-secondary-50">{headingNode}</h4>
          <p className="text-sm text-secondary-500 dark:text-secondary-300 mb-0">{descriptionText}</p>
        </div>
      </button>
    </div>
  )
}

export default function Autocomplete({
  searchClient,
  indexName,
  updateOnChange = false,
  ...autocompleteProps
}: AutocompleteProps) {
  const searchRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const panelRootRef = useRef<Root | null>(null)
  const rootRef = useRef<HTMLElement | null>(null)

  const { isAskAiEnabled, isModalOpen, askAiAgent } = useSearch()
  const { query, refine } = useSearchBox()
  const { t } = useTranslations()
  const { currentLocale } = useLocale()
  const askAiHref = localizedPath('/ask-ai/', currentLocale)
  // Build the suggestion node here (inside the Inertia tree) so the detached
  // createRoot below doesn't try to call usePage().
  const renderSuggestion = (queryStr: string) => {
    if (!isAskAiEnabled) return null
    return (
      <AskAiSuggestion
        query={queryStr}
        titleText={t('Search for {query} in Ask AI').replace('{query}', queryStr)}
        headingNode={interpolate(t('Discover more about {query} with Ask AI'), {
          query: <span className="text-primaryBlue">{queryStr}</span>,
        })}
        descriptionText={t('Explore this conversational AI search for detailed responses about Bitwarden')}
        askAiHref={askAiHref}
        agentType={askAiAgent}
      />
    )
  }

  useEffect(() => {
    // Bug fix: Randomly the query value is empty while the user types when `updateOnChange` is true.
    // This is a workaround to ensure the input value is set correctly.
    const input = searchRef.current?.querySelector('input.aa-Input') as HTMLInputElement | null
    if (input) {
      const value = input.value
      if (!query && value) {
        refine(value)
      }
    }
  }, [query])

  const focusInput = useCallback(() => {
    const input = searchRef.current?.querySelector('input.aa-Input') as HTMLInputElement | null
    if (input) {
      input.focus()
    }
  }, [])

  // Auto focus input when search modal opens
  useEffect(() => {
    if (isModalOpen) {
      const id = setTimeout(() => {
        focusInput()
      }, 200)

      return () => {
        clearTimeout(id)
      }
    }
  }, [isModalOpen])

  useEffect(() => {
    if (query) return

    setTimeout(() => {
      focusInput()
    }, 200)

    const onKeyDown = (event: any): void => {
      if (event?.key === '/' || (event?.key?.toLowerCase() === 'k' && (event.ctrlKey || event.metaKey))) {
        event.preventDefault()
        focusInput()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // const querySuggestionsInType = createQuerySuggestionsPlugin({
  //   searchClient,
  //   indexName: `${indexName}_query_suggestions`,
  //   getSearchParams() {
  //     if (!type) {
  //       return []
  //     }

  //     return recentSearches.data.getAlgoliaSearchParams({
  //       hitsPerPage: 6,
  //       facetFilters: [`${indexName}.facets.exact_matches.type:${type}`],
  //     })
  //   },
  //   categoryAttribute: [indexName, 'facets', 'exact_matches', 'type'],
  //   transformSource({ source }) {
  //     return {
  //       ...source,
  //       sourceId: 'querySuggestionsPluginInType',
  //       onSelect({ item }) {
  //         setInstantSearchUiState({
  //           query: item.query,
  //           type: item.__autocomplete_qsCategory,
  //         })
  //       },
  //       getItems(params) {
  //         if (!type) {
  //           return []
  //         }

  //         return source.getItems(params)
  //       },
  //       templates: {
  //         ...source.templates,
  //         header({ items }) {
  //           return items.length > 0 && <div>In {type}</div>
  //         },
  //       },
  //     }
  //   },
  // })

  useEffect(() => {
    const recentSearches = createLocalStorageRecentSearchesPlugin({
      key: 'instantsearch',
      limit: 5,
      transformSource({ source }) {
        return {
          ...source,
          onSelect(params) {
            refine(params.item.label)
            // setType(params.item.category || '', types, refine)
          },
          getItems(params) {
            return !params.query?.length ? source.getItems(params) : []
          },
          templates: {
            ...source.templates,
            header(params) {
              return !params.state.query?.length && params.items?.length ? getSubjectHeading('Recent searches') : ''
            },
          },
        }
      },
    })

    const suggestions = createQuerySuggestionsPlugin({
      searchClient,
      indexName: `${indexName}_query_suggestions`,
      getSearchParams() {
        return recentSearches.data?.getAlgoliaSearchParams({
          hitsPerPage: 12,
          //   facetFilters: type ? [`${indexName}.facets.exact_matches.type:${type}`] : null,
        })
      },
      // categoryAttribute: [indexName, 'facets', 'exact_matches', 'type'],
      transformSource({ source }) {
        return {
          ...source,
          sourceId: 'querySuggestionsPlugin',
          onSelect(params) {
            refine(params.item.query)
            // setType(params.item.__autocomplete_qsCategory || '', types, refine)
          },
          getItems(params) {
            return source.getItems(params)
          },
          templates: {
            ...source.templates,
            header(params) {
              return !params.state.query?.length && params.items?.length ? getSubjectHeading('Popular searches') : ''
            },
          },
        }
      },
    })

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: searchRef.current as HTMLElement,
      panelContainer: panelRef.current as HTMLElement,
      initialState: { query },
      plugins: [recentSearches, suggestions],
      onReset() {
        refine('')
      },
      onStateChange({ state, prevState }) {
        if (updateOnChange && state.query !== prevState.query) {
          refine(state.query)
        }
        if (!state.isOpen) {
          panelRef.current?.classList.add('hidden')
        } else {
          panelRef.current?.classList.remove('hidden')
        }
      },
      onSubmit({ event, state }) {
        event.preventDefault()
        if (query !== state.query) {
          refine(state.query)
        }
      },

      renderer: { createElement, Fragment, render: () => {} },
      render({ children, state }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }
        panelRootRef.current.render(
          <>
            {isAskAiEnabled && state.query && renderSuggestion(state.query)}
            {children}
          </>
        )
      },
      renderNoResults({ state }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }
        panelRootRef.current.render(<>{state.query && renderSuggestion(state.query)}</>)
      },
    })

    return () => autocompleteInstance.destroy()
  }, [isAskAiEnabled])

  return (
    <>
      <div className={autocompleteProps.classNames?.bwSearchContainer || 'md:pt-4'} ref={searchRef} />
      <div>
        <div className={autocompleteProps.classNames?.bwPanelContainer || 'md:pt-4 [&_>div]:z-10'} ref={panelRef} />
      </div>
    </>
  )
}

function getSubjectHeading(text: ReactNode) {
  return <div className="text-primaryBlue text-xs">{text}</div>
}
